import { useState } from 'react';
import { operatorAuthService } from '../services/operatorAuth';
import { Operator } from '../types/database';
import '../styles/operator-login.css';

interface OperatorLoginProps {
  onSuccess: (operator: Operator) => void;
}

export default function OperatorLogin({ onSuccess }: OperatorLoginProps) {
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleNumberClick = (num: string) => {
    if (code.length < 4) {
      setCode(code + num);
      setError(false);
    }
  };

  const handleBackspace = () => {
    setCode(code.slice(0, -1));
    setError(false);
  };

  const handleClear = () => {
    setCode('');
    setError(false);
  };

  const handleSubmit = async () => {
    if (code.length !== 4) {
      return;
    }

    setLoading(true);
    setError(false);

    try {
      const operator = await operatorAuthService.validateOperatorCode(code);

      if (operator) {
        // Store operator in session
        operatorAuthService.storeOperatorSession(operator);
        // Call success callback
        onSuccess(operator);
      } else {
        setError(true);
        setCode('');
      }
    } catch (err) {
      console.error('Error during operator login:', err);
      setError(true);
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="operator-login-screen" data-testid="operator-login">
      <div className="operator-login-box">
        <h2>Operator Login</h2>
        <p className="operator-login-subtitle">Enter your 4-digit operator code</p>

        {/* Code Display */}
        <div className="code-display">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className={`code-dot ${i < code.length ? 'filled' : ''}`}>
              {i < code.length ? '●' : '○'}
            </div>
          ))}
        </div>

        {/* Virtual Numpad */}
        <div className="numpad">
          <div className="numpad-row">
            <button data-testid="numpad-1" className="numpad-btn" onClick={() => handleNumberClick('1')} disabled={loading}>1</button>
            <button data-testid="numpad-2" className="numpad-btn" onClick={() => handleNumberClick('2')} disabled={loading}>2</button>
            <button data-testid="numpad-3" className="numpad-btn" onClick={() => handleNumberClick('3')} disabled={loading}>3</button>
          </div>
          <div className="numpad-row">
            <button data-testid="numpad-4" className="numpad-btn" onClick={() => handleNumberClick('4')} disabled={loading}>4</button>
            <button data-testid="numpad-5" className="numpad-btn" onClick={() => handleNumberClick('5')} disabled={loading}>5</button>
            <button data-testid="numpad-6" className="numpad-btn" onClick={() => handleNumberClick('6')} disabled={loading}>6</button>
          </div>
          <div className="numpad-row">
            <button data-testid="numpad-7" className="numpad-btn" onClick={() => handleNumberClick('7')} disabled={loading}>7</button>
            <button data-testid="numpad-8" className="numpad-btn" onClick={() => handleNumberClick('8')} disabled={loading}>8</button>
            <button data-testid="numpad-9" className="numpad-btn" onClick={() => handleNumberClick('9')} disabled={loading}>9</button>
          </div>
          <div className="numpad-row">
            <button data-testid="numpad-clear" className="numpad-btn numpad-btn-secondary" onClick={handleClear} disabled={loading}>Clear</button>
            <button data-testid="numpad-0" className="numpad-btn" onClick={() => handleNumberClick('0')} disabled={loading}>0</button>
            <button data-testid="numpad-backspace" className="numpad-btn numpad-btn-secondary" onClick={handleBackspace} disabled={loading}>←</button>
          </div>
          <div className="numpad-row">
            <button
              data-testid="numpad-submit"
              className="numpad-btn numpad-btn-submit"
              onClick={handleSubmit}
              disabled={loading || code.length !== 4}
            >
              {loading ? 'Checking...' : 'Submit'}
            </button>
          </div>
        </div>

        {error && <p className="code-error">Invalid operator code</p>}
      </div>
    </div>
  );
}
